<template>
  <div>
    <div v-if="!authReady" class="text-center py-16">
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>

    <login-first v-else-if="!uid" />

    <div v-else-if="!conventionLoaded" class="text-center py-16">
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>

    <v-container v-else-if="!convention">
      <v-card outlined class="rounded-lg text-center pa-8">
        <v-icon size="56" color="grey lighten-1" v-text="'mdi-notebook-remove-outline'"></v-icon>
        <div class="text-h6 mt-3">Convention not found</div>
        <p class="body-2 grey--text text--darken-1 mt-2">
          This convention does not exist, or it was removed by its members.
        </p>
        <v-btn color="primary" text to="/convention" v-text="'Back to conventions'"></v-btn>
      </v-card>
    </v-container>

    <v-container v-else-if="!isMember">
      <v-card outlined class="rounded-lg text-center pa-8">
        <v-icon size="56" color="primary" v-text="'mdi-account-lock-outline'"></v-icon>
        <div class="text-h6 mt-3">You are not a member yet</div>
        <p class="body-2 grey--text text--darken-1 mt-2">
          Join <strong>{{ convention.name }}</strong> from the convention
          directory first, then you can browse its contract tree.
        </p>
        <v-btn color="primary" text to="/convention" v-text="'Back to conventions'"></v-btn>
      </v-card>
    </v-container>

    <v-container v-else>
      <v-card outlined class="rounded-lg">
        <v-toolbar flat color="white">
          <v-btn
            icon
            to="/convention"
            aria-label="Back to conventions"
          >
            <v-icon v-text="'mdi-arrow-left'"></v-icon>
          </v-btn>
          <v-toolbar-title class="font-weight-bold text-truncate">
            {{ convention.name }}
          </v-toolbar-title>
          <v-chip
            v-if="isAdmin"
            class="ml-2"
            x-small
            label
            color="primary"
            text-color="white"
            v-text="'Admin'"
          ></v-chip>
          <v-spacer></v-spacer>
          <v-btn
            icon
            :disabled="trail.length === 0"
            aria-label="Back to start"
            @click="goRoot"
          >
            <v-icon v-text="'mdi-home-outline'"></v-icon>
          </v-btn>
          <v-btn icon aria-label="Followers" @click="openFollowers">
            <v-icon v-text="'mdi-account-multiple-outline'"></v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider></v-divider>

        <v-card-text>
          <p
            v-if="convention.desc"
            class="body-2 grey--text text--darken-1 mb-4"
            v-text="convention.desc"
          ></p>

          <div class="d-flex align-center flex-wrap">
            <span class="caption grey--text mr-2" v-text="'Bid trail'"></span>
            <v-chip
              small
              label
              outlined
              @click="goRoot"
              v-text="'Start'"
            ></v-chip>
            <template v-for="(step, index) in trail">
              <v-icon
                :key="'sep' + index"
                small
                class="mx-1 grey--text"
                v-text="'mdi-chevron-right'"
              ></v-icon>
              <v-chip
                :key="'step' + index"
                small
                label
                color="#E6DA7A"
                class="black--text"
                @click="jumpTo(index)"
                v-text="step.name"
              ></v-chip>
            </template>
          </div>

          <div class="d-flex align-center mt-6 mb-1">
            <span class="caption grey--text" v-text="'Continuations'"></span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="isAdmin"
              x-small
              text
              color="primary"
              :disabled="!connected"
              @click="openAddContract"
            >
              <v-icon left x-small v-text="'mdi-plus'"></v-icon>
              Add contract
            </v-btn>
          </div>

          <v-progress-linear
            v-if="groupLoading"
            indeterminate
            color="primary"
          ></v-progress-linear>

          <div
            v-else-if="contracts.length === 0"
            class="text-center py-8 px-4"
          >
            <v-icon size="40" color="grey lighten-1" v-text="'mdi-cards-outline'"></v-icon>
            <div class="body-2 grey--text text--darken-1 mt-2">
              {{
                trail.length
                  ? "No continuation after " +
                    trail[trail.length - 1].name +
                    " yet."
                  : "This convention has no contract yet."
              }}
            </div>
            <v-btn
              v-if="isAdmin"
              class="mt-3"
              text
              color="primary"
              :disabled="!connected"
              @click="openAddContract"
              v-text="'Add the first contract'"
            ></v-btn>
          </div>

          <v-list v-else dense class="py-0">
            <v-list-item
              v-for="contract in contracts"
              :key="contract.name"
              @click="openContract(contract)"
            >
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ contract.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="contract-desc">
                  {{ contract.desc || "No description" }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon>
                <v-icon small v-text="'mdi-chevron-right'"></v-icon>
              </v-list-item-icon>
              <div v-if="isAdmin" @click.stop>
                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      aria-label="Contract options"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small v-text="'mdi-dots-vertical'"></v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="openEditContract(contract)">
                      <v-list-item-icon>
                        <v-icon small v-text="'mdi-pencil-outline'"></v-icon>
                      </v-list-item-icon>
                      <v-list-item-title v-text="'Edit contract'"></v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="askDeleteContract(contract)">
                      <v-list-item-icon>
                        <v-icon small color="error" v-text="'mdi-delete-outline'"></v-icon>
                      </v-list-item-icon>
                      <v-list-item-title class="error--text" v-text="'Delete contract'"></v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </v-list-item>
          </v-list>
        </v-card-text>

        <template v-if="!connected">
          <v-divider></v-divider>
          <div class="pa-2 caption grey--text text--darken-1 text-center">
            Checking updates&hellip; changes are disabled until you reconnect.
          </div>
        </template>
      </v-card>
    </v-container>

    <v-dialog
      v-model="contractDialog"
      max-width="440"
      @keydown.enter="saveContract"
    >
      <v-card class="rounded-lg">
        <v-card-title
          class="text-h6"
          v-text="editingName ? 'Edit contract' : 'Add contract'"
        ></v-card-title>
        <v-card-text>
          <v-text-field
            v-model="contractForm.name"
            label="Contract"
            placeholder="1NT"
            prepend-inner-icon="mdi-cards-outline"
            :error-messages="contractError"
            hint="1-7 followed by C/D/H/S/NT, or Pass, X, XX"
            persistent-hint
            autofocus
            @input="contractError = ''"
          ></v-text-field>
          <v-text-field
            v-model="contractForm.desc"
            class="mt-4"
            label="Meaning"
            placeholder="15-17 HCP, balanced"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="editingName"
            text
            color="error"
            @click="askDeleteContract(null)"
            v-text="'Delete'"
          ></v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closeContractDialog" v-text="'Cancel'"></v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="saving"
            @click="saveContract"
            v-text="editingName ? 'Save' : 'Add'"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card class="rounded-lg">
        <v-card-title class="text-h6" v-text="'Delete contract?'"></v-card-title>
        <v-card-text>
          <strong v-text="deleteTarget ? deleteTarget.name : ''"></strong>
          and its meaning will be removed from this convention. Contracts that
          continue from it stay in the database but are no longer reachable.
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="deleteDialog = false" v-text="'Cancel'"></v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            depressed
            :loading="saving"
            @click="deleteContract"
            v-text="'Delete'"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="4000"
      v-text="snackbar.text"
    ></v-snackbar>
  </div>
</template>

<script>
import { db } from "@/App.vue";
import {
  ref,
  onValue,
  get,
  set,
  remove,
  push,
} from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  compareContracts,
  validateContractName,
} from "@/utils/convention";
import LoginFirst from "./LoginFirst.vue";

export default {
  name: "ConventionDetailVue",

  components: { LoginFirst },

  data: () => ({
    id: "",
    uid: null,
    authReady: false,
    connected: true,
    convention: null,
    conventionLoaded: false,
    rootGroupId: null,
    trail: [],
    contracts: [],
    groupLoading: false,
    stopAuth: null,
    stopConnection: null,
    stopConvention: null,
    stopGroup: null,
    contractDialog: false,
    editingName: null,
    contractForm: { name: "", desc: "" },
    contractError: "",
    deleteDialog: false,
    deleteTarget: null,
    saving: false,
    snackbar: { show: false, text: "", color: "success" },
  }),

  computed: {
    isMember() {
      if (!this.convention || !this.uid) return false;
      if (this.convention.owner === this.uid) return true;
      return !!(this.convention.follower && this.convention.follower[this.uid]);
    },

    isAdmin() {
      if (!this.convention || !this.uid) return false;
      if (this.convention.owner === this.uid) return true;
      return !!(
        this.convention.follower && this.convention.follower[this.uid] === "admin"
      );
    },

    currentGroupId() {
      if (this.trail.length) {
        return this.trail[this.trail.length - 1].link;
      }
      return this.rootGroupId;
    },
  },

  watch: {
    convention(value) {
      if (!value) return;
      if (!this.trail.length) {
        this.rootGroupId = value.link || null;
      }
    },

    currentGroupId(value) {
      this.attachGroup(value);
    },
  },

  created() {
    this.id = this.$route.params.id;

    this.stopAuth = onAuthStateChanged(getAuth(), (user) => {
      this.uid = user ? user.uid : null;
      this.authReady = true;
    });

    this.stopConnection = onValue(
      ref(db, ".info/connected"),
      (snapshot) => {
        this.connected = snapshot.val() === true;
      }
    );

    this.stopConvention = onValue(
      ref(db, "conventions/" + this.id),
      (snapshot) => {
        this.convention = snapshot.val();
        this.conventionLoaded = true;
      }
    );
  },

  beforeDestroy() {
    if (this.stopAuth) this.stopAuth();
    if (this.stopConnection) this.stopConnection();
    if (this.stopConvention) this.stopConvention();
    if (this.stopGroup) this.stopGroup();
  },

  methods: {
    attachGroup(groupId) {
      if (this.stopGroup) {
        this.stopGroup();
        this.stopGroup = null;
      }
      this.contracts = [];
      if (!groupId) {
        this.groupLoading = false;
        return;
      }

      this.groupLoading = true;
      this.stopGroup = onValue(
        ref(db, "group_contracts/" + groupId),
        (snapshot) => {
          const value = snapshot.val() || {};
          this.contracts = Object.keys(value)
            .map((name) => {
              const contract = value[name] || {};
              return {
                name: name,
                desc: contract.desc || "",
                link: contract.link || null,
              };
            })
            .sort(compareContracts);
          this.groupLoading = false;
        }
      );
    },

    openContract(contract) {
      this.trail.push({ name: contract.name, link: contract.link });
    },

    jumpTo(index) {
      this.trail.splice(index + 1);
    },

    goRoot() {
      if (!this.trail.length) return;
      this.trail = [];
    },

    openFollowers() {
      this.$router.push({
        name: "Convention Followers",
        params: { id: this.id },
      });
    },

    openAddContract() {
      this.editingName = null;
      this.contractForm = { name: "", desc: "" };
      this.contractError = "";
      this.contractDialog = true;
    },

    openEditContract(contract) {
      this.editingName = contract.name;
      this.contractForm = { name: contract.name, desc: contract.desc };
      this.contractError = "";
      this.contractDialog = true;
    },

    closeContractDialog() {
      this.contractDialog = false;
      this.editingName = null;
      this.contractError = "";
    },

    async saveContract() {
      if (!this.isAdmin || this.saving) return;
      const check = validateContractName(this.contractForm.name);
      if (!check.valid) {
        this.contractError = check.error;
        return;
      }

      const desc = (this.contractForm.desc || "").trim();
      this.saving = true;

      try {
        if (!this.editingName) {
          const groupId = await this.ensureGroup();
          const existing = await get(
            ref(db, "group_contracts/" + groupId + "/" + check.name)
          );
          if (existing.exists()) {
            this.contractError = "Already Exists";
            return;
          }

          const link = push(ref(db, "group_contracts")).key;
          await set(
            ref(db, "group_contracts/" + groupId + "/" + check.name),
            { desc: desc, link: link }
          );
          this.notify("Contract added");
        } else {
          const groupId = this.currentGroupId;
          const originalName = this.editingName;
          const contractRef = ref(
            db,
            "group_contracts/" + groupId + "/" + originalName
          );
          const snapshot = await get(contractRef);
          const old = snapshot.val() || {};

          if (check.name !== originalName) {
            const existing = await get(
              ref(db, "group_contracts/" + groupId + "/" + check.name)
            );
            if (existing.exists()) {
              this.contractError = "Already Exists";
              return;
            }

            const link = old.link || push(ref(db, "group_contracts")).key;

            // Write the new name before dropping the old one so a failed write
            // never loses the continuation group.
            await set(
              ref(db, "group_contracts/" + groupId + "/" + check.name),
              { desc: desc, link: link }
            );
            await remove(contractRef);

            this.trail = this.trail.map((step) => {
              if (step.name !== originalName) return step;
              return { name: check.name, link: step.link };
            });
          } else {
            await set(contractRef, { desc: desc, link: old.link || null });
          }
          this.notify("Contract updated");
        }

        this.closeContractDialog();
      } catch (error) {
        this.contractError = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    },

    // Contract creation needs a group; the root group is created lazily the
    // same way the Android app does it.
    async ensureGroup() {
      if (this.currentGroupId) return this.currentGroupId;
      const newGroup = push(ref(db, "group_contracts"));
      await set(ref(db, "conventions/" + this.id + "/link"), newGroup.key);
      return newGroup.key;
    },

    askDeleteContract(contract) {
      this.deleteTarget = contract || {
        name: this.editingName,
      };
      this.contractDialog = false;
      this.deleteDialog = true;
    },

    async deleteContract() {
      if (!this.isAdmin || !this.deleteTarget || this.saving) return;
      this.saving = true;
      try {
        await remove(
          ref(
            db,
            "group_contracts/" +
              this.currentGroupId +
              "/" +
              this.deleteTarget.name
          )
        );
        this.deleteDialog = false;
        this.closeContractDialog();
        this.deleteTarget = null;
        this.notify("Contract deleted");
      } catch (error) {
        this.notify(this.errorMessage(error), "error");
      } finally {
        this.saving = false;
      }
    },

    errorMessage(error) {
      return (error && error.message) || "Something went wrong";
    },

    notify(text, color) {
      this.snackbar = {
        show: true,
        text: text,
        color: color || "success",
      };
    },
  },
};
</script>

<style scoped>
.contract-desc {
  white-space: normal;
}

.v-list-item {
  border-radius: 8px;
}
</style>
